import React, { useEffect, useState } from 'react';
import './Notifications.css';
import { useNavigate, useLocation } from 'react-router-dom';
import api from './api';
import { useNotificationsContext } from './context/NotificationContext';

import backIcon from './assets/backspace-icon.png';
import deleteIcon from './assets/delete-outline-icon.png';
import markAllIcon from './assets/markall-icon.png';
import ReadMoreText from './common/ReadMoreText';
import NotificationSkeleton from './components/skeleton/NotificationSkeleton';
import DeleteNotificationDrawer from './components/popups/DeleteNotificationDrawer';
import NotificationsMenuDrawer from './components/popups/NotificationsMenuDrawer';
import DeleteAllNotificationsDrawer from './components/popups/DeleteAllNotificationsDrawer';
import tipNudgeIcon from './assets/tipnudge-icon.png';
import LoadingSpinner from './components/common/LoadingSpinner';
import AwarenessNotificationCard from './components/notifications/AwarenessNotificationCard';
import BadgeRewardDrawer from './components/popups/BadgeRewardDrawer';


// --- Order/escrow & dispute redirect helpers (safe, additive) ---
const ORDER_KEYWORDS = ['placed an order', 'order created', 'new order'];
const SELLER_CONFIRMED_KEYWORDS = [
  'has confirmed the order',
  'seller confirmed',
  'please confirm to complete',
];

const ORDER_CANCELLED_KEYWORDS = [
  'cancelled the order',
  'order cancelled',
  'has been cancelled',
  'auto-cancelled',
  'auto cancelled',
  'failed to confirm in time',
  'refund is on the way',
];

const DISPUTE_OPENED_KEYWORDS = [
  'dispute has been opened',
  'opened a dispute',
  'dispute opened',
];

const DISPUTE_RESOLVED_KEYWORDS = [
  'dispute resolved',
  'you won the case',
  'you lost the case',
  'case resolved',
];

function getEscrowOrOrderId(n) {
  return (
    n?.orderId ||
    n?.escrowId ||
    n?.entityId ||
    n?.meta?.orderId ||
    n?.meta?.escrowId ||
    n?.data?.orderId ||
    n?.payload?.orderId ||
    n?.order?._id ||
    n?.escrow?._id ||
    null
  );
}

function getShortOrderCodeFromMessage(n) {
  const msg = (n?.content || n?.text || n?.message || '') + '';
  const m = msg.match(/order\s*#\s*([A-Za-z0-9]+)/i);
  return m?.[1] || null;
}

function isOrderFlowNotification(n) {
  const t = (n?.type || '').toLowerCase();
  const msg = (n?.content || n?.text || n?.message || '').toLowerCase();

  if (
    t === 'order' ||
    t === 'escrow.order_placed' ||
    t === 'escrow.seller_confirmed' ||
    t === 'escrow.order_cancelled' ||
    t === 'escrow.auto_cancelled'
  ) {
    return true;
  }

  if (ORDER_KEYWORDS.some((k) => msg.includes(k))) return true;
  if (SELLER_CONFIRMED_KEYWORDS.some((k) => msg.includes(k))) return true;
  if (ORDER_CANCELLED_KEYWORDS.some((k) => msg.includes(k))) return true;

  return false;
}

function isDisputeOpenedNotification(n) {
  const t = (n?.type || '').toLowerCase();
  const msg = (n?.content || n?.text || n?.message || '').toLowerCase();
  if (t === 'escrow.dispute_opened' || t === 'dispute_opened') return true;
  return DISPUTE_OPENED_KEYWORDS.some((k) => msg.includes(k));
}

function isDisputeResolvedNotification(n) {
  const t = (n?.type || '').toLowerCase();
  const msg = (n?.content || n?.text || n?.message || '').toLowerCase();
  if (t === 'escrow.dispute_resolved' || t === 'dispute_resolved') return true;
  return DISPUTE_RESOLVED_KEYWORDS.some((k) => msg.includes(k));
}

function resolveNotifAvatar(notification, isSponsorship) {

// ✅ Custom avatar for tip nudges
if (notification.type === 'tip_nudge') {
  return tipNudgeIcon;
}

  if (notification.icon) {
    return require(`./assets/${notification.icon}`);
  }

  if (notification.avatar && typeof notification.avatar === 'string') {
    if (notification.avatar.startsWith('/assets/')) {
      const file = notification.avatar.replace('/assets/', '');
      return require(`./assets/${file}`);
    }

    if (notification.avatar.startsWith('http')) return notification.avatar;
  }

  if (isSponsorship) return require('./assets/campaign-icon.png');

  const t = (notification.text || '').toLowerCase();

  if (
    t.includes('merchant application has been rejected') ||
    t.includes('merchant account has been suspended') ||
    t.includes('your account has been suspended') ||
    t.includes('merchant status has been revoked') ||
    t.includes('merchant access has been revoked')
  ) {
    return require('./assets/fail-icon.png');
  }

  if (
    t.includes('merchant account has been reinstated') ||
    t.includes('your account has been reinstated')
  ) {
    return require('./assets/pass-icon.png');
  }

  if (
    t.includes('your store was approved') ||
    t.includes('tap to activate') ||
    t.includes('merchant access is active')
  ) {
    return require('./assets/mystore-icon.png');
  }

  return notification.profilePic || require('./assets/placeholder-profile.png');
}

function Notifications({ user, setUnreadNotifCount, onStoreApprovedClick }) {

const {
  notifications,
  loading,
  error,
  hasFetched,
  hasMore,
  loadingMore,
  unreadCount,
  fetchNotifications,
  refreshNotifications,
  loadMoreNotifications,
  markNotificationRead,
  markGroupRead,
  openAwareness,
  hideAwareness,
  updatePiNewsPreference,
  markAllAsRead,
  deleteNotification,
  deleteAllNotifications,
} = useNotificationsContext();

  const navigate = useNavigate();
  const location = useLocation();
  const fromMarketplace = location.state?.from === 'marketplace';
  const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState(null);
  const [showNotifMenuDrawer, setShowNotifMenuDrawer] = useState(false);
  const [showDeleteAllDrawer, setShowDeleteAllDrawer] = useState(false);

const [badgeAwards, setBadgeAwards] = useState([]);
const [badgeCycleKey, setBadgeCycleKey] = useState('');
const [selectedBadgeIndex, setSelectedBadgeIndex] = useState(0);
const [badgeDrawerOpen, setBadgeDrawerOpen] = useState(false);
const [badgePreviewLoading, setBadgePreviewLoading] = useState(false);
const [badgePreviewError, setBadgePreviewError] = useState('');


  useEffect(() => {
    if (!user?.username) return;

    if (!hasFetched) {
      fetchNotifications(user.username);
    } else {
      refreshNotifications(user.username);
    }
  }, [user?.username, hasFetched, fetchNotifications, refreshNotifications]);

  useEffect(() => {
    setUnreadNotifCount?.(unreadCount);
  }, [unreadCount, setUnreadNotifCount]);

  useEffect(() => {
    const scrollArea = document.getElementById('notifications-scroll');
    if (!scrollArea) return;

    const handleScroll = () => {
      if (
        scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 200 &&
        hasMore &&
        !loadingMore
      ) {
        loadMoreNotifications(user.username);
      }
    };

    scrollArea.addEventListener('scroll', handleScroll);
    return () => scrollArea.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, notifications, user?.username, loadMoreNotifications]);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead(user.username);
  };



const handleAwarenessOpen = async (notification) => {
  const campaignId = notification?.campaignId || notification?._id;
  const articleId = notification?.article?._id || notification?.article?.id;

  if (!campaignId || !articleId) {
    console.error('❌ Awareness notification is missing campaign or article information.');
    return;
  }

  try {
    await openAwareness(campaignId, { openedFrom: 'notifications' });

    navigate(`/news/${articleId}`, {
      state: {
        article: notification.article,
        fromNotification: true,
        awarenessCampaignId: campaignId,
      },
    });
  } catch (err) {
    console.error('❌ Failed to open awareness notification:', err);
  }
};

const handleAwarenessHide = async (notification) => {
  const campaignId = notification?.campaignId || notification?._id;

  if (!campaignId) return;

  try {
    await hideAwareness(campaignId);
  } catch (err) {
    console.error('❌ Failed to hide awareness notification:', err);
  }
};

const handleAwarenessPreferenceChange = async (value) => {
  try {
    await updatePiNewsPreference(value);
  } catch (err) {
    console.error('❌ Failed to update Pi News preference:', err);
  }
};



const openBadgeRewardPreview = async (notif) => {
  const match = String(notif.link || '')
    .match(/^\/rewards\/cycle\/(cycle-\d{5,})\/?$/);

  if (!match) {
    setBadgePreviewError('This reward preview is missing its cycle information.');
    return;
  }

  const cycleKey = match[1];

  setBadgePreviewError('');
  setBadgePreviewLoading(true);

  try {
    const { data } = await api.get(
      `/rewards/cycle/${cycleKey}/my-awards`
    );

    if (!data?.ok || !Array.isArray(data.items) || data.items.length === 0) {
      throw new Error('No badge rewards were returned.');
    }

    setBadgeAwards(data.items);
    setBadgeCycleKey(cycleKey);
    setSelectedBadgeIndex(0);
    setBadgeDrawerOpen(true);
  } catch (err) {
    console.error('Failed to load badge reward preview:', err);
    setBadgePreviewError('Could not load your badge rewards. Tap the notification to try again.');
  } finally {
    setBadgePreviewLoading(false);
  }
};


const handleNotificationClick = async (notif) => {
  try {
    if (notif.kind === 'awareness') {
      await handleAwarenessOpen(notif);
      return;
    }

    if (notif.kind === 'group') {
      await markGroupRead({
        toUser: user.username,
        type: notif.type,
        postId: notif.postId || null,
      });

      if (notif.postId) {
        navigate(`/post/${notif.postId}`);
      }

      return;
    }

    await markNotificationRead(notif._id);

if (notif.type === 'reward_cycle') {
  await openBadgeRewardPreview(notif);
  return;
}

      if (isOrderFlowNotification(notif)) {
        const focusId = getEscrowOrOrderId(notif);
        navigate('/orders', {
          state: {
            fromNotification: true,
            focusOrderId: focusId || null,
          },
        });
        return;
      }

      if (isDisputeOpenedNotification(notif)) {
        const fullId = getEscrowOrOrderId(notif);
        if (fullId && /^[a-f0-9]{24}$/i.test(fullId)) {
          navigate(`/order/${fullId}`, {
            state: { fromNotification: true, focusDispute: true },
          });
        } else {
          const shortCode = getShortOrderCodeFromMessage(notif);
          navigate('/orders', {
            state: { fromNotification: true, focusShortCode: shortCode || null },
          });
        }
        return;
      }

      if (isDisputeResolvedNotification(notif)) {
        const fullId = getEscrowOrOrderId(notif);
        if (fullId && /^[a-f0-9]{24}$/i.test(fullId)) {
          navigate(`/dispute-decision/${fullId}`, {
            state: { fromNotification: true, resolved: true },
          });
        } else {
          const shortCode = getShortOrderCodeFromMessage(notif);
          navigate('/orders', {
            state: {
              fromNotification: true,
              focusShortCode: shortCode || null,
              resolved: true,
            },
          });
        }
        return;
      }


if (notif.type === 'follow' && notif.fromUser) {
  navigate(`/user/${notif.fromUser}`);
  return;
}

// 📰 News reply notification
if (notif.type === 'news_reply' && notif.articleId) {
  navigate(`/news/${notif.articleId}`, {
    state: {
      fromNotification: true,
      notificationType: 'news_reply',
      newsCommentId: notif.newsCommentId || null,
      newsReplyId: notif.newsReplyId || null,
    },
  });

  return;
}

if (notif.disputeId) {

        try {
          const res = await api.get(`/disputes/${notif.disputeId}`);
          const orderId = res.data?.orderId;
          const finalOrderId = orderId?._id || orderId;
          if (finalOrderId) {
            navigate(`/dispute-decision/${finalOrderId}`);
          } else {
            alert('Cannot open dispute — order information missing.');
          }
        } catch (err) {
          console.error('❌ Failed to fetch dispute from notification:', err);
          alert('Failed to open dispute. Please try again later.');
        }
        return;
      }

      const message = (notif.content || notif.text || notif.message || '').toLowerCase();
      if (
        message.includes('your store was approved') ||
        message.includes('tap to activate') ||
        message.includes('reactivate your store') ||
        message.includes('merchant access is active')
      ) {
        navigate('/merchant-payment');
        return;
      }

      if (notif.link && notif.link.includes('/sponsor-payment') && notif.type === 'sponsorship') {
        const url = new URL(notif.link, window.location.origin);
        const productId = url.searchParams.get('productId');
        if (productId) {
          navigate(`/sponsor-payment/${productId}`, { state: { productId } });
        } else {
          alert('Unable to open sponsored payment. Product info is missing.');
        }
        return;
      }



{
  const lowerType = (notif.type || '').toLowerCase();
  const msg = (notif.content || notif.text || notif.message || '').toLowerCase();


// 🛒 Product repost notification
// Opens the feed post that reposted the product.
if (lowerType === 'product_repost' || msg.includes('reposted your product')) {
  const repostPostId = notif.repostPostId || notif.postId || null;

  if (repostPostId) {
    navigate(`/post/${repostPostId}`, {
      state: {
        fromNotification: true,
        notificationType: lowerType,
      },
    });
    return;
  }

  // Last fallback only: if repost post id is missing, open the product.
  const productId =
    notif.productId ||
    notif.relatedProduct ||
    null;

  if (productId) {
    navigate(`/product/${productId}`, {
      state: { fromNotification: true },
    });
    return;
  }
}

  // 🔁 Normal post repost notification
  // Opens the repost post, same as before.
  if (lowerType === 'repost' || msg.includes('reposted your post')) {
    const repostPostId = notif.repostPostId || null;

    if (repostPostId) {
      navigate(`/post/${repostPostId}`);
      return;
    }

    if (notif.postId) {
      navigate(`/post/${notif.postId}`);
      return;
    }
  }

  const postTypes = [
    'mention',
    'like',
    'comment',
    'tip',
    'tip_nudge',
    'reply',
    'reply_comment',
  ];

  if (notif.postId && postTypes.includes(lowerType)) {
    navigate(`/post/${notif.postId}`, {
      state: {
        fromNotification: true,
        notificationType: lowerType,
        targetUsername: notif.targetUsername || null,
        openTipDrawer: lowerType === 'tip_nudge',
        tipAmount: lowerType === 'tip_nudge' ? 0.5 : null,
      },
    });
    return;
  }
}



    } catch (err) {
      console.error('❌ Error marking notification as read:', err);
    }
  };

  const handleDeleteNotification = async (notifObjOrId) => {
    await deleteNotification(user.username, notifObjOrId);
  };

  const handleDeleteAll = () => {
    setShowNotifMenuDrawer(false);
    setShowDeleteAllDrawer(true);
  };

  const confirmDeleteAll = async () => {
    try {
      await deleteAllNotifications(user.username);
      setShowDeleteAllDrawer(false);
    } catch (err) {
      console.error('Error deleting all notifications:', err);
    }
  };

  return (
    <div className="notifications-container">
      <div className="notifications-top-nav">
        <div className="top-nav-left">
          <img
            src={backIcon}
            alt="Back"
            className="back-icon-notifications"
            onClick={() => navigate(fromMarketplace ? '/marketplace' : '/home')}
          />
        </div>
        <h2 className="notifications-header-center">Notifications</h2>

        <div className="top-nav-right">
          <button
            type="button"
            className="notifications-menu-btn"
            onClick={() => setShowNotifMenuDrawer(true)}
            aria-label="Open notifications menu"
          >
            <img
              src={require('./assets/dots-icon.png')}
              alt="menu"
              className="notifications-dots-icon"
            />
          </button>
        </div>
      </div>

 

<div className="notifications-scroll-area" id="notifications-scroll">
  {badgePreviewLoading && (
    <p className="no-notifications">Opening your badge rewards...</p>
  )}

  {badgePreviewError && (
    <p className="no-notifications" role="alert">{badgePreviewError}</p>
  )}

  {loading && !hasFetched ? (
    <NotificationSkeleton />
  ) : notifications.length === 0 ? (
    <p className="no-notifications">No notifications yet.</p>
  ) : (
    <div className="notifications-list">


{notifications.map((notification) => {
  if (notification.kind === 'awareness') {
    return (
      <AwarenessNotificationCard
        key={`awareness-${notification.campaignId || notification._id}`}
        notification={notification}
        onOpen={handleAwarenessOpen}
        onHide={handleAwarenessHide}
        onPreferenceChange={handleAwarenessPreferenceChange}
      />
    );
  }

  const isSponsorship = notification.type === 'sponsorship';

  return (
    <div


                  key={`${notification.kind || 'single'}-${notification._id}`}
                  className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <img
                    src={resolveNotifAvatar(notification, isSponsorship)}
                    alt="avatar"
                    className="notification-avatar"
                  />

                  <div className="notification-text">
                    <ReadMoreText
                      text={notification.content || notification.text || notification.message || ''}
                      maxLines={2}
                    />
                    <div className="notification-time">
                      {new Date(notification.createdAt).toLocaleString()}
                    </div>
                  </div>

                  <img
                    src={deleteIcon}
                    alt="Delete"
                    className="notification-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTargetToDelete(notification.kind === 'group' ? notification : notification._id);
                      setShowDeleteDrawer(true);
                    }}
                  />
                </div>
              );
            })}

{loadingMore && <LoadingSpinner size="medium" />}

            {!hasMore && !loadingMore && notifications.length > 0 && (
              <div className="end-of-feed-msg"><p>You've reached the end.</p></div>
            )}
          </div>
        )}
      </div>

      {showDeleteDrawer && (
        <DeleteNotificationDrawer
          onClose={() => setShowDeleteDrawer(false)}
          onConfirm={async () => {
            await handleDeleteNotification(targetToDelete);
            setShowDeleteDrawer(false);
            setTargetToDelete(null);
          }}
        />
      )}

      <NotificationsMenuDrawer
        isOpen={showNotifMenuDrawer}
        onClose={() => setShowNotifMenuDrawer(false)}
        onMarkAllAsRead={handleMarkAllAsRead}
        onDeleteAll={handleDeleteAll}
      />

      {showDeleteAllDrawer && (
        <DeleteAllNotificationsDrawer
          onClose={() => setShowDeleteAllDrawer(false)}
          onConfirm={confirmDeleteAll}
        />
      )}


<BadgeRewardDrawer
  isOpen={badgeDrawerOpen}
  awards={badgeAwards}
  selectedIndex={selectedBadgeIndex}
  cycleKey={badgeCycleKey}
  onSelect={setSelectedBadgeIndex}
  onClose={() => setBadgeDrawerOpen(false)}
/>

    </div>
  );
}

export default Notifications;
